import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Field } from 'react-final-form';

import FormSelect from 'Shared/components/FormComponents/FormSelect';
import { createFormField } from 'Shared/utils/createFormField';
import StyledCheckbox from 'Shared/components/Checkboxes/StyledCheckbox/StyledCheckbox';
import SlackLogo from 'Shared/components/Logos/SlackLogo';
import MicrosoftTeamsLogo from 'Shared/components/Logos/MicrosoftTeamsLogo';
import * as api from 'Shared/api';
import Spinner from 'Shared/components/Spinner';
import { NotificationDestinationSlugs } from 'Shared/constants/types';
import { getNotificationDestinations } from 'Shared/actions/notifications';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

import './index.scss';

type Props = {
  value: boolean;
  onChange: Function;
};

function ShareToChat({ onChange, value }: Props) {
  const { tr } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [shareOnChatStatus, setShareOnChatStatus] = useState(null);
  const notificationDestinations = useSelector(state => state.notification_destinations);
  const dispatch = useDispatch();

  useEffect(() => {
    async function fetchShareOnChatStatus() {
      try {
        setIsLoading(true);
        await dispatch(getNotificationDestinations());
        const response = await api.getShareOnChatStatus();
        if (response.status === 200) setShareOnChatStatus(response.data);
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
      }
    }

    fetchShareOnChatStatus();
  }, []);

  const slackNotificationDestination = notificationDestinations.find(
    ({ slug }) => slug === NotificationDestinationSlugs.slack
  );
  const microsoftTeamsNotificationDestination = notificationDestinations.find(
    ({ slug }) => slug === NotificationDestinationSlugs.microsoftTeams
  );
  const selectedNotificationDestination = notificationDestinations.find(
    ({ slug }) => slug === shareOnChatStatus?.notificationDestination
  );

  const handleConnectSlackClick = useCallback(
    event => {
      event.preventDefault();
      (window as any).analytics.track('Clicked Connect Chat in Kudos Modal', { button: 'Connect Slack' });
      if (slackNotificationDestination?.path) window.location.href = slackNotificationDestination.path;
    },
    [slackNotificationDestination]
  );

  const handleConnectMicrosoftTeamsClick = useCallback(
    event => {
      event.preventDefault();
      (window as any).analytics.track('Clicked Connect Chat in Kudos Modal', { button: 'Connect Microsoft Teams' });
      if (microsoftTeamsNotificationDestination?.path) {
        // For Microsoft Teams we want to open a new tab, because it takes us out of WorkPatterns
        const a = document.createElement('a');
        a.href = microsoftTeamsNotificationDestination.path;
        a.target = '_blank';
        a.click();
      }
    },
    [microsoftTeamsNotificationDestination]
  );

  const shareToChatToggle = () => {
    if (value) {
      onChange(null);
    } else {
      onChange(shareOnChatStatus.notificationDestination);
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex flex-column h-100">
        <Spinner activeIf spinnerStyles={{ width: '20px', height: '20px' }} />
      </div>
    );
  }

  // If the request to load their shareOnChatStatus failed
  if (!shareOnChatStatus) {
    return null;
  }

  // If they need to install the bot to their team
  if (shareOnChatStatus.status === 'teams_bot_not_installed_to_team') {
    return (
      <React.Fragment>
        <span className="mr-2" style={{ color: '#F88341' }}>
          {tr('perform.modals.give_recognition.teams_bot_install_message')}
        </span>
        <WpButton variant="secondary" onClick={handleConnectMicrosoftTeamsClick}>
          <MicrosoftTeamsLogo width={19} className="mr-1" /> {tr('perform.modals.give_recognition.go_to_teams_button')}
        </WpButton>
      </React.Fragment>
    );
  }

  // If they need to integrate a chat notification destination
  if (shareOnChatStatus.status === 'no_integration') {
    return (
      <div className="mb-4">
        <p className="mb-1">{tr('perform.modals.give_recognition.share_integration_message')}</p>
        <WpButtonGroup>
          <WpButton variant="secondary" onClick={handleConnectSlackClick}>
            <SlackLogo width={16} className="mr-1" style={{ marginBottom: '2px' }} />{' '}
            {tr('perform.modals.give_recognition.share_on_slack_button')}
          </WpButton>
          <WpButton variant="secondary" onClick={handleConnectMicrosoftTeamsClick}>
            <MicrosoftTeamsLogo width={19} className="mr-1" />{' '}
            {tr('perform.modals.give_recognition.share_on_teams_button')}
          </WpButton>
        </WpButtonGroup>
      </div>
    );
  }

  return (
    <div className="form-share-to-chat d-flex flex-column flex-sm-row">
      <StyledCheckbox
        id="shareToChat"
        name="shareToChat"
        className="my-2 mr-2"
        label={tr('perform.modals.give_recognition.share_to_destination', selectedNotificationDestination.name)}
        checked={value}
        onChange={shareToChatToggle}
      />
      <Field
        name="chatOptions.channel"
        className="form-share-to-chat-selector"
        placeholder={tr('perform.modals.give_recognition.select_channel_placeholder')}
        disabled={!value}
        menuPortalTarget={document.body}
        customStyles={{ menuPortal: base => ({ ...base, zIndex: '1050' }) }}
        options={shareOnChatStatus.channelOptions}
        component={FormSelect}
      />
    </div>
  );
}

export default createFormField(ShareToChat);
